import * as React from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog-sticky-footer';
import { Checkbox } from '@components/ui/checkbox';
import { api } from '@/trpc/react';
import { UPSCALE_YOUR_LIKED_PHOTO_DIALOG_ID } from '@/atoms/upscaleYourLikedPhotoDialog';
import { Zap } from 'lucide-react';
import { CheckmarkIcon } from '@components/Icons/checkmark';
import { ModelImage } from '@acme/db';
import { UPSCALE_SERVICES } from '@acme/lib';
import { useToast } from '@components/ui/use-toast';
import { useTrackClientEvent } from '@analytics/trackClientEvent';
import Image from 'next/image';
import { motion } from 'framer-motion';
interface UpscaleYourLikedPhotoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  modelId: string;
  image: ModelImage;
  albumId: string;
}

interface DontShowAgainProps {
  onCheckedChange: (checked: boolean) => void;
  checked: boolean;
}

const DontShowAgain = ({ onCheckedChange, checked }: DontShowAgainProps) => {
  return (
    <div className="flex items-center gap-2 mx-auto w-fit">
      <Checkbox
        id="like-photo-tips"
        checked={checked}
        onCheckedChange={checked => onCheckedChange(checked as boolean)}
      />
      <label
        htmlFor="like-photo-tips"
        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
      >
        Don't show again
      </label>
    </div>
  );
};

export function UpscaleYourLikedPhotoDialog({ open, onOpenChange, image, albumId }: UpscaleYourLikedPhotoDialogProps) {
  const [isChecked, setIsChecked] = React.useState(false);
  const utils = api.useUtils();
  const { toast } = useToast();
  const { trackEvent } = useTrackClientEvent();

  const dismissMutation = api.userDismissedItem.dismissItem.useMutation({
    onSuccess: () => {
      utils.userDismissedItem.hasDismissedItem.invalidate({
        itemId: UPSCALE_YOUR_LIKED_PHOTO_DIALOG_ID,
        itemType: 'dialog'
      });
    }
  });

  const upscaleMutation = api.modelImage.requestUpscale.useMutation({
    onMutate: () => {
      trackEvent('photoAction', {
        value: 'upscale',
        metadata: {
          albumId,
          imageId: image.id
        }
      });
    },
    onSuccess: () => {
      if (albumId) {
        utils.albums.invalidate();
        utils.modelImage.invalidate();
      }
    },
    onError: error => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    },
    onSettled: () => {
      setTimeout(() => {
        if (albumId) {
          utils.albums.invalidate();
          utils.modelImage.invalidate();
        }
      }, 1000);
    }
  });

  const handleDismiss = () => {
    if (isChecked) {
      dismissMutation.mutate({
        itemId: UPSCALE_YOUR_LIKED_PHOTO_DIALOG_ID,
        itemType: 'dialog'
      });
    }
    onOpenChange(false);
  };

  const handleUpscale = () => {
    // Start upscale
    upscaleMutation.mutate({
      imageId: image.id,
      service: UPSCALE_SERVICES.DEFAULT
    });

    // Close dialog and handle dismiss
    handleDismiss();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col gap-0 p-0 h-[100dvh] md:h-auto md:max-h-[85vh] sm:max-w-lg [&>button:last-child]:hidden">
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 flex-1 flex flex-col gap-2 h-full ">
            {/* <IconContainer className="mb-2" variant="default" borderless>
              <Zap size={24} className="text-accent-foreground fill-accent-foreground" />
            </IconContainer> */}
            <div className="shrink-0 flex flex-col mb-6 w-48 h-48 md:w-1/2 md:h-fit max-h-[256px] aspect-square mx-auto relative overflow-hidden rounded-lg bg-muted">
              {/* <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-[#E2B3FC] z-10" /> */}
              <motion.div
                className="absolute top-0 left-[10%] right-[10%] border-l-2 border-fuchsia-300 h-full bg-background/60 to-transparent z-10 backdrop-blur-[1px]"
                initial={{ x: 40 }}
                animate={{ x: [40, 100] }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  repeatType: 'reverse',
                  ease: 'easeInOut'
                }}
              >
                <div className="flex items-center justify-center rounded-full bg-white w-8 h-8 aspect-square absolute top-1/2 -left-4 shadow-md">
                  <Zap className="text-black stroke-black fill-black bg-white rounded-full h-3 w-3" />
                </div>
              </motion.div>
              {/* <div className="absolute bg-black/50 w-1/2 h-full right-0 top-0 bottom-0 backdrop-blur-sm" /> */}
              <Image
                src={image.imageUrl}
                alt="Upscale your liked photo"
                width={256}
                height={256}
                className="mx-auto h-full w-full object-cover object-top scale-150 aspect-square"
              />
            </div>
            <DialogHeader className="flex flex-col gap-2 space-y-0 mb-6">
              <div className="mb-6">
                <DialogTitle className="text-3xl text-left">Improve this photo with Upscale</DialogTitle>
                <DialogDescription className="text-lg text-left text-foreground">
                  <span className="font-bold text-accent">It's free</span> and only takes a minute.
                </DialogDescription>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-2">
                  <CheckmarkIcon className="bg-success stroke-success-foreground" />
                  <p className="text-left text-foreground">Upscale the resolution to 4K</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckmarkIcon className="bg-success stroke-success-foreground" />
                  <p className="text-left text-foreground">Sharper details </p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckmarkIcon className="bg-success stroke-success-foreground" />
                  <p className="text-left text-foreground">More realistic textures</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckmarkIcon className="bg-success stroke-success-foreground" />
                  <p className="text-left text-foreground leading-normal">Better resemblance</p>
                </div>
              </div>
            </DialogHeader>
            <div className="mt-auto">
              <DontShowAgain checked={isChecked} onCheckedChange={setIsChecked} />
            </div>
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 border-t border-border px-6 py-4">
          <div className="flex items-center gap-2 w-full sm:grid sm:grid-cols-2">
            <DialogClose asChild>
              <Button variant="secondary" className="flex-1" onClick={handleDismiss}>
                Maybe Later
              </Button>
            </DialogClose>
            <Button variant="accent" className="flex-1" onClick={handleUpscale}>
              Upscale Now
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
